import type { ReferenceCell } from 'entities/document';

type StyledCell = ReferenceCell & { classes?: string[] };

export interface StyleIssue {
  row: number;
  col: number;
  label: string;
  header: string;
  styles: string[];
  lost: boolean;
}

const norm = (v: unknown) =>
  String(v ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const raw = (v: unknown) => String(v ?? '').trim();

export const cellStyles = (cell: StyledCell | null | undefined): string[] =>
  [cell?.format, ...(cell?.classes ?? []), cell?.styling, cell?.background, cell?.border].filter((s): s is string => !!s);

const rowLabels = (grid: StyledCell[][]) => grid.map((row) => norm(row?.[0]?.value));
export const gridWidth = (grid: { length?: number }[]) => grid.reduce((widest, row) => Math.max(widest, row?.length ?? 0), 0);
const columnHeaders = (grid: StyledCell[][]) => (grid[0] ?? []).map((cell) => norm(cell?.value));

const shifted = (oldKeys: string[], newKeys: string[], i: number) => {
  const before = oldKeys[i];
  const after = newKeys[i];
  if (!before || before === after) return false;
  return newKeys.includes(before) || (!!after && oldKeys.includes(after));
};

export function styleCarryReport(oldGrid: StyledCell[][] = [], newGrid: StyledCell[][] = []): StyleIssue[] {
  const oldRows = rowLabels(oldGrid);
  const newRows = rowLabels(newGrid);
  const oldCols = columnHeaders(oldGrid);
  const newCols = columnHeaders(newGrid);
  const columnsChanged = gridWidth(oldGrid) !== gridWidth(newGrid);
  const issues: StyleIssue[] = [];

  oldGrid.forEach((row, r) => {
    (row ?? []).forEach((cell, c) => {
      const styles = cellStyles(cell);
      if (styles.length === 0) return;
      const lost = !newGrid[r]?.[c];
      if (!lost && !shifted(oldRows, newRows, r) && !(columnsChanged && shifted(oldCols, newCols, c))) return;
      issues.push({ row: r, col: c, label: raw(oldGrid[r]?.[0]?.value), header: raw(oldGrid[0]?.[c]?.value), styles, lost });
    });
  });

  return issues;
}
